import _get from 'lodash/get'
import _round from 'lodash/round'
import _sortBy from 'lodash/sortBy'
import moment from 'moment'

import Date from '@/shared/filters/Date'
import Period from '@/shared/filters/Period'
import { API } from '@/shared/plugins/Api/API'
import LogStream from '@/shared/plugins/LogStream'

// Prefect WS status events carry a sparse stage tree (only the patched stage
// indices / action nodes, entries without _id): merge it into the loaded tree
// by index. Full trees (classic engine events, the single-job endpoint —
// every stage has an _id) replace the tree as before.
const mergeStages = (current, incoming) => {
  if (!incoming) return current
  if (incoming.every(stage => stage?._id)) return incoming
  // Sparse tree without a base: drop it, so load() still fetches the full one
  if (!current?.length) return current
  return current.map((stage, index) => {
    const patch = incoming[index]
    if (!patch) return stage
    const actions = { ...stage.actions }
    Object.entries(patch.actions || {}).forEach(([nodeId, actionPatch]) => {
      actions[nodeId] = { ...actions[nodeId], ...actionPatch }
    })
    return { ...stage, ...patch, actions }
  })
}

class Job extends API {
  constructor (options) {
    super('DPE')
    this.execution = null
    this.status = null
    this.execution_id = options.execution_id
    this.update(options)
  }

  update (options) {
    // A Job is one execution: never apply a payload from another execution
    // (e.g. a stale fetch racing a newer run)
    if (options.execution_id && this.execution_id && options.execution_id !== this.execution_id) return this
    this.service = options.service || this.service || 'operator'
    this.type = options.type || this.type
    this.id = options.id || this.id
    this.execution_id = options.execution_id || this.execution_id
    this._id = options._id || this._id
    this.created_at = options.created_at || this.created_at
    this.updated_at = options.updated_at || this.updated_at
    this.end_at = options.end_at || this.end_at
    this.fpu = options.fpu || this.fpu
    this.environment = options.environment || this.environment || {}
    this.display_name = options.display_name || this.display_name || ''
    this.event = options.event || this.event || {}

    // Job queue time info
    this.queue = options.queue || this.queue || {}
    this.queue.start_at = this.queue.start_at || null
    this.queue.end_at = this.queue.end_at || null

    // Job build time info
    this.build = options.build || this.build || {}
    this.build.start_at = this.build.start_at || null
    this.build.end_at = this.build.end_at || null

    // Job provisioning time info
    this.provisioning = options.provisioning || this.provisioning || {}
    this.provisioning.start_at = this.provisioning.start_at || null
    this.provisioning.end_at = this.provisioning.end_at || null

    this.status = options.status || this.status
    this.kube_status = options.kube_status || this.kube_status
    // v3/jobs list rows exclude execution.stages: never let a slim row wipe
    // the stage tree received via WS status events or the single-job endpoint
    if (options.execution) {
      this.execution = { ...options.execution, stages: mergeStages(this.execution?.stages, options.execution.stages) }
    }

    this.metrics = options.metrics || this.metrics || {}
    this.metrics_info = options.metrics_info || this.metrics_info || {}
    // Carries spark_ui: tells whether a live Spark UI service is exposed
    this.tags = options.tags || this.tags || {}
    // Served from the durable history: slim record only (no live metrics/stages)
    this.archived = options.archived || this.archived

    return this
  }

  // Load details from server
  async load (force = false) {
    if (_get(this, 'execution.stages') && !force) return
    const status = await this.request({
      method: 'get',
      url: `v3/jobs/${this.execution_id}`,
      params: {
        metrics: true
      }
    })
    this.update(status)
  }

  get formattedExecutionId () {
    return (this.execution_id?.slice(-7)) || '-'
  }

  get isActive () {
    return ['PENDING', 'SUBMITTED', 'QUEUED', 'BUILDING', 'PROVISIONING', 'PROCESSING'].includes(this.status)
  }

  get stopped () {
    return this.status === 'STOPPED'
  }

  get progress () {
    return _get(this, 'execution.progress') ? Math.ceil(parseFloat(_get(this, 'execution.progress'))) : 0
  }

  stage (stageId) {
    const stages = _get(this, 'execution.stages')
    if (!stages) return null
    return stages.find(stage => stage._id === stageId)
  }

  action (stageId, actionId) {
    const stage = this.stage(stageId)
    if (!stage) return null
    return _get(stage, `actions[${actionId}]`)
  }

  get lastExecution () {
    if (!this.startTime) return '-'
    const time = moment().unix() - this.startTime.unix()
    return Period(time)
  }

  //  Queued time + Run time + Build time
  get duration () {
    return this.executionTime
  }

  get executionTime () {
    return this.runTime + this.buildTime + this.queuedTime
  }

  get executionDate () {
    return this.startTime
  }

  get startTime () {
    return Date(this.created_at)
  }

  get endTime () {
    return Date(this.end_at)
  }

  // Run Time
  get runTime () {
    if (!this.runStartTime) return null
    if (this.runEndTime) {
      return moment(this.runEndTime).unix() - moment(this.runStartTime).unix()
    }
    if (this.endTime) {
      return moment(this.endTime).unix() - moment(this.runStartTime).unix()
    }
    return moment().unix() - moment(this.runStartTime).unix()
  }

  get runStartTime () {
    return this.provisioningStartTime
  }

  get runEndTime () {
    if (!_get(this, 'execution.end_time')) return null
    return Date(this.execution.end_time)
  }

  // Queued Time
  get queued () {
    return ['SUBMITTED', 'QUEUED'].includes(this.status)
  }

  get queuedTime () {
    if (!this.queuedStartTime) return null
    if (this.queuedEndTime) {
      return moment(this.queuedEndTime).unix() - moment(this.queuedStartTime).unix()
    }
    // if no queuedEndTime and nature not 'queued' => return null
    if (this.nature !== 'queued') return null
    return moment().unix() - moment(this.queuedStartTime).unix()
  }

  get queuedStartTime () {
    if (!this.queue.start_at) return null
    return Date(this.queue.start_at)
  }

  get queuedEndTime () {
    if (!this.queue.end_at) return null
    return Date(this.queue.end_at)
  }

  // Build Time
  get buildTime () {
    if (!this.buildStartTime) return null
    if (this.buildEndTime) {
      return moment(this.buildEndTime).unix() - moment(this.buildStartTime).unix()
    }
    return moment().unix() - moment(this.buildStartTime).unix()
  }

  get buildStartTime () {
    if (!this.build.start_at) return null
    return Date(this.build.start_at)
  }

  get buildEndTime () {
    if (!this.build.end_at) return null
    return Date(this.build.end_at)
  }

  // Provisioning Time
  get provisioningTime () {
    if (!this.provisioningStartTime) return null
    if (this.provisioningEndTime) {
      return moment(this.provisioningEndTime).unix() - moment(this.provisioningStartTime).unix()
    }
    return moment().unix() - moment(this.provisioningStartTime).unix()
  }

  get provisioningStartTime () {
    if (!this.provisioning.start_at) return null
    return Date(this.provisioning.start_at)
  }

  get provisioningEndTime () {
    if (!this.provisioning.end_at) return null
    return Date(this.provisioning.end_at)
  }

  get resources () {
    if (this.fpu) {
      return `${this.fpu.instances}x${this.fpu.size}`
    }
    return '1x1'
  }

  // Possible return : SUCCESS, FAILED, STOPPED, TIMEOUT, PENDING, PROCESSING, PROVISIONING, QUEUED, SUBMITTED, BUILDING
  get state () {
    if (this.status === 'PROVISIONING') return 'PENDING'
    return this.status
  }

  // Determines the job nature if it's running, in queue or done (past).
  get nature () {
    if (['QUEUED', 'SUBMITTED', 'PENDING', 'BUILDING', 'PROVISIONING'].includes(this.status)) return 'queued'
    if (this.isActive) return 'running'
    return 'past'
  }

  async logs () {
    const service = this.service === 'builder' ? this.service : 'operator'
    return new LogStream(this.id, this.execution_id, service, 'DPE')
  }

  get cpuRamUsage () {
    const cpuRam = ['cpu', 'mem']
    const cpuRamUsage = {
      cpu: {
        max: this.metrics_info?.max_cpu ? _round(this.metrics_info?.max_cpu_usage / this.metrics_info?.max_cpu * 100, 1) : 0,
        usage: {}
      },
      mem: {
        max: this.metrics_info?.max_mem ? _round(this.metrics_info?.max_mem_usage / this.metrics_info?.max_mem * 100, 1) : 0,
        usage: {}
      }
    }

    // We block max to 100%
    if (cpuRamUsage.cpu.max > 100) cpuRamUsage.cpu.max = 100
    if (cpuRamUsage.mem.max > 100) cpuRamUsage.mem.max = 100

    for (const metricKey in this.metrics) {
      cpuRam.forEach(metric => {
        const chart = []
        let max = 0
        let min = 100
        let sumValue = 0
        const maxValue = parseFloat(this.metrics[metricKey][`max_${metric}`])
        const startTimeUnix = this.startTime.unix()
        const endTimeUnix = this.runEndTime?.unix()

        if (this.metrics[metricKey][metric].length > 0) {
          this.metrics[metricKey][metric].forEach(m => {
            const value = parseFloat(m.value)
            const valuePercent = _round(value / maxValue * 100, 1)
            if (valuePercent > max) max = valuePercent
            if (valuePercent < min) min = valuePercent
            const timeInSecondFromStart = m.timestamp - startTimeUnix
            chart.push([timeInSecondFromStart, valuePercent])
            sumValue += valuePercent
          })
          chart.unshift([0, 0])
          // Close the line at the run end only when it extends the series: metrics
          // can be sampled past the recorded end (or the end can be missing), and
          // the closing point would otherwise draw the line backwards.
          const endFromStart = endTimeUnix - startTimeUnix
          if (endFromStart > chart[chart.length - 1][0]) chart.push([endFromStart, chart[chart.length - 1][1]])
          cpuRamUsage[metric].usage[metricKey] = {
            maxToReach: this.metrics[metricKey][`max_${metric}`],
            chart,
            max,
            min,
            avgPercent: _round(sumValue / this.metrics[metricKey][metric].length, 1)
          }
        } else {
          cpuRamUsage[metric].usage[metricKey] = {
            maxToReach: this.metrics[metricKey][`max_${metric}`],
            chart,
            max: 0,
            min: 0,
            avgPercent: 0
          }
        }
      })
    }

    return cpuRamUsage
  }

  get cpuUsage () {
    return this.cpuRamUsage.cpu
  }

  get ramUsage () {
    return this.cpuRamUsage.mem
  }

  async stop () {
    return this.request({
      method: 'DELETE',
      url: `v3/jobs/${this.type}/${this.execution_id}/stop`
    })
  }
}

class Jobs extends API {
  async list (options = {}) {
    const output = (jobs) => {
      const byExecId = {}
      _sortBy(jobs, ['created_at']).forEach(job => {
        byExecId[job.execution_id] = byExecId[job.execution_id] ? byExecId[job.execution_id].update(job) : new Job(job)
      })

      return Object.values(byExecId)
    }

    const allJobs = await this.paginateCursor({
      method: 'get',
      url: 'v3/jobs',
      // Larger pages → fewer cursor round-trips (the history is loaded fully)
      params: { limit: options.limit || 500 },
      onProgress: (jobs) => {
        if (options?.onProgress) options.onProgress(output(jobs))
      }
    })

    return output(allJobs)
  }

  // Durable job history (slim rows), windowed + filtered server-side; cursor-paginated
  async listHistory (options = {}) {
    const output = (jobs) => {
      const byExecId = {}
      _sortBy(jobs, ['created_at']).forEach(job => {
        byExecId[job.execution_id] = byExecId[job.execution_id] ? byExecId[job.execution_id].update(job) : new Job(job)
      })
      return Object.values(byExecId)
    }

    const filter = { ...(options.filter || {}) }
    // Days window sets created_at — unless the caller already scoped it (e.g. heatmap drill-down)
    if (options.days && !filter.created_at) {
      const start = moment().subtract(options.days - 1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      const end = moment().add(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      filter.created_at = { $gte: start, $lt: end }
    }

    const params = {
      pagination: true,
      limit: options.limit || 300,
      order_by: 'created_at',
      order: 'desc',
      filter: JSON.stringify(filter)
    }
    if (options.cursor) params.cursor = options.cursor

    const response = await this.request({ method: 'get', url: 'v3/jobs/history', originalResponse: true, params })

    // 206 + a next cursor means more pages are loadable on demand
    const cursor = response.headers?.['x-pagination-cursor-next'] || null
    return { jobs: output(response.data || []), cursor, hasMore: response.status === 206 && !!cursor }
  }

  // Pre-aggregated overview stats (server-side). tz: IANA zone for day/hour bucketing
  async overview (days = 7, tz) {
    const timezone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    return this.request({
      method: 'get',
      url: 'v3/jobs/overview',
      params: { days, tz: timezone }
    })
  }

  // Generic composable job stats (group_by + metrics) over the history.
  // spec = { from, to, days, tz, filter, queries: [{ key, group_by, metrics, sort, limit }] }
  async stats (spec = {}) {
    return this.request({
      method: 'post',
      url: 'v3/jobs/stats',
      data: spec
    })
  }

  // Load only one job by execution_id
  async loadJob (executionId) {
    const job = await this.request({
      method: 'get',
      url: `v3/jobs/${executionId}`,
      params: {
        metrics: true
      }
    })
    return new Job(job)
  }
}

export default Jobs

export {
  Jobs,
  Job
}
